import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRepairParts } from "@/context/RepairPartsContext";
import { RepairPart, RepairPartFormData } from "@/types/stock";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useRepairShopContext } from "@/context/RepairShopContext";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Package, AlertTriangle, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface StockMovement {
  id: string;
  repairPartId: string;
  partName: string;
  quantity: number;
  costAtTime: number;
  repairId: string;
  customerName: string;
  createdAt: Date;
}

const RepairPartsManagement: React.FC = () => {
  const { t } = useTranslation();
  const { repairShop } = useRepairShopContext();
  const { repairParts, loading, addRepairPart, updateRepairPart, deleteRepairPart } = useRepairParts();



  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState<RepairPart | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  const [formData, setFormData] = useState<RepairPartFormData>({
    name: "",
    description: "",
    cost: 0,
    stockQuantity: 0,
    minStockLevel: 0,
    isActive: true,
  });



  const handleDeleteClick = async (part: RepairPart) => {
    try {
      await deleteRepairPart(part.id);
    } catch (error) {
      console.error("Error deleting repair part:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      cost: 0,
      stockQuantity: 0,
      minStockLevel: 0,
      isActive: true,
    });
    setEditingPart(null);
  };

  const handleOpenDialog = (part?: RepairPart) => {
    if (part) {
      setEditingPart(part);
      setFormData({
        name: part.name,
        description: part.description || "",
        cost: part.cost,
        stockQuantity: part.stockQuantity,
        minStockLevel: part.minStockLevel,
        isActive: part.isActive,
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingPart) {
        await updateRepairPart(editingPart.id, formData);
      } else {
        await addRepairPart(formData);
      }
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving repair part:", error);
    }
  };

  const filteredParts = repairParts.filter((part) =>
    part.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    part.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeParts = filteredParts.filter((p) => p.isActive);
  const lowStockParts = activeParts.filter((p) => p.stockQuantity <= p.minStockLevel);

  // Fetch stock movements for a specific part
  const fetchStockMovements = async (partId?: string) => {
    if (!repairShop) return;
    setLoadingMovements(true);
    try {
      let query = supabase
        .from("repair_parts_used")
        .select(`
          id,
          repair_part_id,
          quantity,
          cost_at_time,
          repair_id,
          created_at,
          repair_part:repair_parts(name),
          repair:repairs(customer_name)
        `)
        .order("created_at", { ascending: false });

      if (partId) {
        query = query.eq("repair_part_id", partId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const movements: StockMovement[] = (data || []).map((item: any) => ({
        id: item.id,
        repairPartId: item.repair_part_id,
        partName: item.repair_part?.name || "Unknown",
        quantity: item.quantity,
        costAtTime: item.cost_at_time,
        repairId: item.repair_id,
        customerName: item.repair?.customer_name || "Unknown",
        createdAt: new Date(item.created_at),
      }));

      setStockMovements(movements);
    } catch (error) {
      console.error("Error fetching stock movements:", error);
    } finally {
      setLoadingMovements(false);
    }
  };

  // Fetch all stock movements on mount
  useEffect(() => {
    fetchStockMovements();
  }, [repairShop]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t("stock.repairPartsManagement")}</h2>
          <p className="text-muted-foreground">{t("stock.repairParts")}</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="mr-2 h-4 w-4" />
          {t("stock.addRepairPart")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("stock.totalProducts")}</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeParts.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("stock.lowStock")}</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{lowStockParts.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("stock.totalCostValue")}</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {activeParts.reduce((sum, p) => sum + p.cost * p.stockQuantity, 0).toFixed(3)} TND
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stock Movements */}
      <Card>
        <CardHeader>
          <CardTitle>{t("stock.stockMovements")}</CardTitle>
          <CardDescription>
            {t("stock.recentStockOperations")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingMovements ? (
            <div className="text-center py-8">{t("common.loading")}</div>
          ) : stockMovements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t("stock.noStockMovements")}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead>{t("stock.partName")}</TableHead>
                  <TableHead className="text-right">{t("stock.quantity")}</TableHead>
                  <TableHead className="text-right">{t("stock.partCost")}</TableHead>
                  <TableHead>{t("repair.customerName")}</TableHead>
                  <TableHead>{t("stock.operation")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stockMovements.slice(0, 10).map((movement) => (
                  <TableRow key={movement.id}>
                    <TableCell className="text-sm">
                      {format(movement.createdAt, "dd/MM/yyyy HH:mm")}
                    </TableCell>
                    <TableCell className="font-medium">{movement.partName}</TableCell>
                    <TableCell className="text-right text-red-600 font-semibold">
                      -{movement.quantity}
                    </TableCell>
                    <TableCell className="text-right">
                      {movement.costAtTime.toFixed(3)} TND
                    </TableCell>
                    <TableCell>{movement.customerName}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                        {t("stock.stockOut")}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Parts Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle>{t("stock.repairParts")}</CardTitle>
              <CardDescription>
                {t("stock.repairPartsManagement")}
              </CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t("stock.searchProducts")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">{t("common.loading")}</div>
          ) : repairParts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t("stock.noPartsSelected")}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("stock.partName")}</TableHead>
                  <TableHead>{t("stock.partDescription")}</TableHead>
                  <TableHead className="text-right">{t("stock.partCost")}</TableHead>
                  <TableHead className="text-right">{t("stock.stock")}</TableHead>
                  <TableHead className="text-right">{t("stock.minStock")}</TableHead>
                  <TableHead className="text-center">{t("stock.isActive")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredParts.map((part) => (
                  <TableRow
                    key={part.id}
                    className={
                      part.stockQuantity <= part.minStockLevel
                        ? "bg-orange-50 dark:bg-orange-950/20"
                        : ""
                    }
                  >
                    <TableCell className="font-medium">{part.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {part.description || "-"}
                    </TableCell>
                    <TableCell className="text-right">{part.cost.toFixed(3)} TND</TableCell>
                    <TableCell className="text-right">
                      <span
                        className={
                          part.stockQuantity <= part.minStockLevel
                            ? "text-orange-600 font-semibold"
                            : ""
                        }
                      >
                        {part.stockQuantity}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{part.minStockLevel}</TableCell>
                    <TableCell className="text-center">
                      {part.isActive ? "✓" : "✗"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(part)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteClick(part)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>




      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {editingPart ? t("stock.editRepairPart") : t("stock.addRepairPart")}
              </DialogTitle>
              <DialogDescription>
                {editingPart
                  ? t("stock.editRepairPart")
                  : t("stock.addRepairPart")}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">{t("stock.partName")}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">{t("stock.partDescription")}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="cost">{t("stock.partCost")}</Label>
                  <Input
                    id="cost"
                    type="number"
                    step="0.001"
                    min="0"
                    value={formData.cost}
                    onChange={(e) =>
                      setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })
                    }
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="stockQuantity">{t("stock.stock")}</Label>
                  <Input
                    id="stockQuantity"
                    type="number"
                    min="0"
                    value={formData.stockQuantity}
                    onChange={(e) =>
                      setFormData({ ...formData, stockQuantity: parseInt(e.target.value) || 0 })
                    }
                    required
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="minStockLevel">{t("stock.minStock")}</Label>
                <Input
                  id="minStockLevel"
                  type="number"
                  min="0"
                  value={formData.minStockLevel}
                  onChange={(e) =>
                    setFormData({ ...formData, minStockLevel: parseInt(e.target.value) || 0 })
                  }
                  required
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label htmlFor="isActive">{t("stock.isActive")}</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t("common.cancel")}
              </Button>
              <Button type="submit">
                {editingPart ? t("common.save") : t("common.add")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RepairPartsManagement;

